<template>
    <div class="o-p-map">
        <ODMapController />
        <div class="o-p-map__wrapper">
            <ACompassButton
                v-if="componentParams.is_show_compass"
                class="ui ui-map-buttons"
                v-model="gyroScopeBtnState"
                :rotate="compassDegree"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ACompassButton from '../../atoms/a-compass-button/a-compass-button';
import ODMapController from "../../organisms/o-d-map-controller/o-d-map-controller";

import {
    UI_MAP_READY,
    UI_MAP_CLICKED,
    UI_ON_CAMERA_UPDATE
} from "@/shared/components/map-viewer/constants/map-events";

import {
    SET_GYROSCOPE_STATE,
    START_GUIDE_OVERVIEW,
    TURN_ON_DEVICE_ORIENTATION_EVENTS,
    DISABLE_TOUCH_HORIZONTAL_ROTATION
} from '@/shared/components/map-viewer/constants/map-commands';

export default {
    name: "OPMap",
    components: {
        ACompassButton,
        ODMapController
    },
    data() {
        return {
            compassDegree: 0,
            gyroScopeBtnState: true
        }
    },
    watch: {
        gyroScopeBtnState: 'setupGyroFeatures',
    },
    computed: {
        ...mapGetters([
            'getMapConfigProp'
        ]),
        componentParams() {
            return this.getMapConfigProp('components.OPMap.params') || {};
        },
        compassCorrectionValue() {
            return this.getMapConfigProp('compass_correction', 0);
        },
    },
    methods: {
        setupGyroFeatures(){
            this.gyroScopeBtnState ? this.modelOrientation() : this.compassOrientation();
        },
        initMapEventListeners(){
            document.viewApi.register_event('on_camera_update', this.updateOrientation);
        },
        deactivateMapEventListeners(){
            document.viewApi.deregister_event('on_camera_update', this.updateOrientation);
        },
        updateOrientation() {
            // Here we need to provide some performance efficient access to viewApi props and methods
            // Using bus/vuex looks not effective here because of high call rate ~60 times a second
            if(document.viewApi)
                this.compassDegree = this.compassCorrectionValue +  document.viewApi.camera_controller.current_orientation
        },
        modelOrientation() {
            this.initMapEventListeners();
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, false);
            this.$bus.$emit(SET_GYROSCOPE_STATE, false);
            this.$store.commit('setCompassOrientationMode', false);
            window.removeEventListener('deviceorientation', this.addDeviceOrientationListener);
        },
        async compassOrientation() {
            this.deactivateMapEventListeners();

            this.$bus.$emit(SET_GYROSCOPE_STATE, true);
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, true);
            this.$bus.$emit(TURN_ON_DEVICE_ORIENTATION_EVENTS, true);
            this.$store.commit('setCompassOrientationMode', true);

            const isOrientationEventSupport = typeof(DeviceOrientationEvent) !== "undefined" && typeof(DeviceOrientationEvent.requestPermission) === "function";

            if (isOrientationEventSupport) {
                try {
                    const response = await DeviceOrientationEvent.requestPermission();

                    if (response === 'granted') {
                        window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
                    }

                } catch(error) {
                    console.warn(error);
                }
            } else {
                window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
            }
        },
        addDeviceOrientationListener(event) {
            const IOS = navigator.platform.indexOf('iPhone') !== -1;
            if (IOS) {
                this.compassDegree = Math.round(event.webkitCompassHeading) * -1;
            } else {
                this.compassDegree = Math.round(event.alpha) * -1;
            }

            if (this.compassDegree < 0) { this.compassDegree += 360; }
            if (this.compassDegree > 360) { this.compassDegree -= 360; }
        },
        initEventListeners() {
            this.$bus.$on(UI_MAP_READY, this.initMapEventListeners);
            this.$bus.$on(START_GUIDE_OVERVIEW, this.changeToStart);
            this.$bus.$on(UI_MAP_CLICKED, this.onMapClicked);
            window.addEventListener("beforeprint", this.changePrintMode);
            window.addEventListener("afterprint", this.changePrintMode);
        },
    },
    mounted() {
        this.initEventListeners();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
}
</script>

<style lang="scss">
@import "o-p-map";
</style>
